import React from 'react';
import { motion } from 'framer-motion';

import { GridPattern, FloatingElements } from './BackgroundEffects';
import { cn } from '../../lib/utils';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH2 = motion.h2;
const MotionP = motion.p;

interface SectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  containerClassName?: string;
  withBackground?: boolean;
  withPattern?: boolean;
}

export function Section({
  title,
  subtitle,
  children,
  className,
  titleClassName,
  subtitleClassName,
  containerClassName,
  withBackground = false,
  withPattern = false,
  ...props
}: SectionProps) {
  return (
    <section className={cn("relative py-16 md:py-24 overflow-hidden", className)} {...props}>
      {/* Background effects */}
      {withBackground && <FloatingElements />}
      {withPattern && <GridPattern />}
      
      <div className={cn("container mx-auto px-4 relative z-10", containerClassName)}>
        {(title || subtitle) && (
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 md:mb-20 text-center"
          >
            {title && (
              <MotionH2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className={cn(
                  "text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 md:mb-14 leading-tight tracking-normal lg:tracking-tight px-2 py-2",
                  "bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent",
                  "dark:from-white dark:via-blue-200 dark:to-white",
                  titleClassName
                )}
              >
                {title}
              </MotionH2>
            )}
            {subtitle && (
              <MotionP
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={cn("text-lg md:text-xl mb-8 md:mb-10 text-gray-600 dark:text-gray-300", subtitleClassName)}
              >
                {subtitle}
              </MotionP>
            )}
          </MotionDiv>
        )}
        
        <MotionDiv
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {children}
        </MotionDiv>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl" />
    </section>
  );
}


